/**
 * 🔴 W82 §1 / THE PRO WEB STEP 2 — THE REVIEW QUEUE'S ORDER: the TypeScript TWIN of `ReviewQueueOrder.swift`.
 *
 * The rule's SOURCE is the Swift (the phone's Clients tab orders its roster by it); this file is held EQUAL to it on
 * every case of the corpus the Swift prints (`__fixtures__/reviewQueueCorpus.json`, emitted by KATA_EMIT_WEB_TOKENS;
 * gate `w82-2-the-dashboard-queue` fails when the committed corpus is not a fresh emission). Its reasons — the 14-day
 * window from Monday of last week, the day as the unit, the fold by `oneReadingPerDay`, training only, the three-group
 * client order — are in the Swift header and are not restated here.
 *
 * ⚠️ THE FOLD IS `progressHistory.oneReadingPerDay`, CALLED, and the ONE comparison both languages can reproduce
 * exactly is Unicode code-point order — never `localeCompare`, whose answer depends on the browser.
 */

import { oneReadingPerDay, type Entry } from "./progressHistory";

export type QueueRecord = {
  readonly id: string;
  readonly clientId: string;
  /** `workout_sessions.kind` — only `training` is reviewed. */
  readonly kind: string;
  /** Epoch ms: `completed_at`, else the last logged set's time (the roster's "finished"). */
  readonly finish: number;
  readonly workingSets: number;
};

export type QueueClient = {
  readonly id: string;
  readonly name: string;
  readonly needsYou: boolean;
  /** `YYYY-MM-DD` of the last logged set inside the roster's look-back, or null. */
  readonly lastTrainedDay: string | null;
};

export const TRAINING_KIND = "training";

/** Monday of LAST week, 00:00 in the page's zone (`Calendar.current` on the phone). */
export function windowStart(today: number): number {
  const d = new Date(today);
  d.setHours(0, 0, 0, 0);
  const sinceMonday = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - sinceMonday - 7);
  return d.getTime();
}

function earlier(a: QueueRecord, b: QueueRecord): number {
  if (a.finish !== b.finish) return a.finish - b.finish;
  const ia = a.id.toLowerCase(), ib = b.id.toLowerCase();
  return ia < ib ? -1 : ia > ib ? 1 : 0;
}

/** The window's finished training records, one per (client, day) — the fullest, ties to the latest. */
export function finishedDays(records: readonly QueueRecord[], since: number): QueueRecord[] {
  const byClient = new Map<string, QueueRecord[]>();
  for (const r of records) {
    if (r.kind !== TRAINING_KIND || r.finish < since) continue;
    byClient.set(r.clientId, [...(byClient.get(r.clientId) ?? []), r]);
  }
  const kept: QueueRecord[] = [];
  for (const clientId of [...byClient.keys()].sort(codePointCompare)) {
    const mine = [...(byClient.get(clientId) ?? [])].sort(earlier);
    const byId = new Map(mine.map((r) => [r.id, r]));
    const entries: Entry[] = mine.map((r) => ({
      date: r.finish, topWeightKg: null, bestReps: null, bestHoldSeconds: null, volumeKg: 0, sets: r.workingSets,
      performed: [], exerciseInstanceId: r.id, sessionId: r.id, bestE1RMKg: null,
    }));
    const folded = oneReadingPerDay([{ id: "fold", name: "", entries }])[0]?.entries ?? [];
    for (const e of folded) {
      const r = byId.get(e.sessionId);
      if (r !== undefined) kept.push(r);
    }
  }
  return kept.sort(earlier);
}

/** Finished days nobody has reviewed, oldest finish first. */
export function waiting(days: readonly QueueRecord[], reviewed: ReadonlySet<string>): QueueRecord[] {
  return days.filter((d) => !reviewed.has(d.id)).sort(earlier);
}

/** An exercise thread whose last word is the client's (`reviewBody.repliesWaiting`) — W82 §8(6). */
export type ReplyWait = { readonly clientId: string; readonly since: number };

/** The client ids in the queue's order — his priority (§8(6)): a finished workout nobody reviewed (oldest finish first) →
 *  an exercise thread awaiting the coach's reply (oldest wait first) → needs you → everyone else. The Swift twin decides. */
export function clientOrder(clients: readonly QueueClient[], waitingDays: readonly QueueRecord[], replies: readonly ReplyWait[] = []): string[] {
  const oldest = new Map<string, number>();
  for (const r of waitingDays) {
    const o = oldest.get(r.clientId);
    if (o === undefined || r.finish < o) oldest.set(r.clientId, r.finish);
  }
  const oldestReply = new Map<string, number>();
  for (const r of replies) {
    const o = oldestReply.get(r.clientId);
    if (o === undefined || r.since < o) oldestReply.set(r.clientId, r.since);
  }
  const group = (c: QueueClient) => (oldest.has(c.id) ? 0 : oldestReply.has(c.id) ? 1 : c.needsYou ? 2 : 3);
  return [...clients].sort((a, b) => {
    const ga = group(a), gb = group(b);
    if (ga !== gb) return ga - gb;
    if (ga === 0) {
      const oa = oldest.get(a.id) ?? 0, ob = oldest.get(b.id) ?? 0;
      if (oa !== ob) return oa - ob;
    } else if (ga === 1 && (oldestReply.get(a.id) ?? 0) !== (oldestReply.get(b.id) ?? 0)) {
      return (oldestReply.get(a.id) ?? 0) - (oldestReply.get(b.id) ?? 0);
    } else if (ga > 1 && a.lastTrainedDay !== b.lastTrainedDay) {
      if (a.lastTrainedDay === null) return 1;
      if (b.lastTrainedDay === null) return -1;
      return a.lastTrainedDay > b.lastTrainedDay ? -1 : 1;
    }
    if (a.name !== b.name) return codePointCompare(a.name, b.name);
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  }).map((c) => c.id);
}

/** Unicode code-point order — Swift's `unicodeScalars` comparison, exactly. */
export function codePointCompare(a: string, b: string): number {
  const pa = [...a].map((ch) => ch.codePointAt(0) ?? 0), pb = [...b].map((ch) => ch.codePointAt(0) ?? 0);
  for (let i = 0; i < Math.min(pa.length, pb.length); i++) {
    const x = pa[i] ?? 0, y = pb[i] ?? 0;
    if (x !== y) return x - y;
  }
  return pa.length - pb.length;
}
